import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { createRoot } from 'react-dom/client';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type AlertDialogProps = {
  open: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  onCloseResult: (result: boolean) => void;
  agreeText?: string;
  disagreeText?: string;
};

export function AlertDialog({
  open,
  title = 'Alert',
  description,
  onCloseResult,
  agreeText,
  disagreeText,
}: AlertDialogProps) {
  const hasActions = Boolean(agreeText || disagreeText);

  return (
    <Dialog
      sx={{ zIndex: 3000 }}
      open={open}
      slots={{ transition: Transition }}
      keepMounted
      onClose={() => onCloseResult(false)}
      aria-describedby="alert-dialog-slide-description"
      sx={{
        '& .MuiDialogTitle-root': {
          color: 'var(--accent-color)'
        }
      }}
    >
      <DialogTitle sx={{ color: '#1976d2' }}>
        {title}
      </DialogTitle>

      <DialogContent>
        {description && (
          <DialogContentText id="alert-dialog-slide-description">
            {description}
          </DialogContentText>
        )}
      </DialogContent>

      {hasActions && (
        <DialogActions>
          {disagreeText && (
            <Button onClick={() => onCloseResult(false)}>
              {disagreeText}
            </Button>
          )}

          {agreeText && (
            <Button onClick={() => onCloseResult(true)} autoFocus>
              {agreeText}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export function showAlertDialog({
  title,
  description,
  agreeText,
  disagreeText,
}: {
  title?: string;
  description?: string;
  agreeText?: string;
  disagreeText?: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const div = document.createElement('div');
    document.body.appendChild(div);

    const root = createRoot(div);

    const handleCloseResult = (result: boolean) => {
      resolve(result);
      setTimeout(() => {
        root.unmount();
        div.remove();
      }, 100);
    };

    root.render(
      <AlertDialog
        open
        title={title}
        description={description}
        agreeText={agreeText}
        disagreeText={disagreeText}
        onCloseResult={handleCloseResult}
      />
    );
  });
}
